/**
 * Business Objective Create Route
 *
 * Create a new business objective using BusinessObjectiveForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { BusinessObjectiveForm } from '../../components/business-objectives';
import {
  createBusinessObjective,
  type CreateBusinessObjectiveInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateBusinessObjectiveFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/business-objectives/new')({
  component: BusinessObjectiveCreatePage,
});

function BusinessObjectiveCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateBusinessObjectiveFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateBusinessObjectiveInput = {
        name: formData.name,
        description: formData.description || undefined,
        objectiveType: formData.objectiveType,
        priority: formData.priority || undefined,
        status: formData.status || undefined,
        startDate: formData.startDate || undefined,
        targetDate: formData.targetDate || undefined,
        timeHorizon: formData.timeHorizon || undefined,
        successCriteria: formData.successCriteria || undefined,
        kpiStatement: formData.kpiStatement || undefined,
        ownerId: formData.ownerId,
        owningBusinessUnitId: formData.owningBusinessUnitId || undefined,
        sponsorId: formData.sponsorId || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      const newEntity = await createBusinessObjective(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessObjectives'],
      });

      // Navigate to the new business objective detail page
      navigate({
        to: `/business-objectives/${newEntity.entityId}` as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/business-objectives', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="business-objective-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Business Objective</h1>
        <p className="forge-typography--body2 page-header-subtitle">Add a new business objective</p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessObjectiveForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
